import { useQueryClient } from "@tanstack/react-query"
import { Modal, notification } from "antd"
import { Download } from "lucide-react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { downloadPersonBrief } from "~/api/pre-meeting"
import Loader from "~/components/base-loader"
import CustomTooltip from "~/components/custom-tooltip"
import { EVENTS } from "~/containers/knowledge-os/pre-meeting/constants"
import useGlobalStore from "~/store/global"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"
import { toPlainStringForApi } from "~/utils/location-helpers"

function sanitizeFileBaseName(name: string): string {
  return (
    toPlainStringForApi(name)
      .replace(/[/\\?%*:|"<>]/g, "-")
      .trim()
      .slice(0, 120) || "brief"
  )
}

async function blobStartsWithPdfMagic(blob: Blob): Promise<boolean> {
  const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer())
  return (
    head.length >= 4 &&
    head[0] === 0x25 &&
    head[1] === 0x50 &&
    head[2] === 0x44 &&
    head[3] === 0x46
  )
}

export default function DownloadPersonBrief({
  personName,
  linkedin_url,
  isDisabled
}: {
  linkedin_url: string
  personName: string
  isDisabled: boolean
}) {
  const { t } = useTranslation()
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()
  const [modalApi, modalContextHolder] = Modal.useModal()
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const queryClient = useQueryClient()

  const onClickDownload = useCallback(async () => {
    const safePersonName = sanitizeFileBaseName(personName)
    const instance = modalApi.confirm({
      title: t("pre_meeting_brief.modal_downloading_pre_meeting_brief"),
      content: (
        <div className="flex items-center justify-center mt-6">
          <Loader />
        </div>
      ),
      okText: t("label_ok"),
      cancelText: t("label_cancel"),
      footer: null,
      centered: true,
      classNames: {
        content: "dark:!bg-dark-2"
      }
    })

    try {
      track(EVENTS.DOWNLOAD_CLICKED, {
        ...getBaseEventProps({ feature: "pre_meeting_brief" }),
        timestamp: Date.now(),
        person_name: safePersonName
      })
      // Reuse the sections already generated when the brief loaded (from the
      // React Query cache) so the PDF matches the screen and we don't regenerate.
      const cachedSections = queryClient
        .getQueriesData<{ section_id?: string } | null>({
          queryKey: ["pmb-section"]
        })
        .filter(
          ([key, data]) =>
            !!data &&
            !!data.section_id &&
            typeof key[2] === "string" &&
            !!linkedin_url &&
            key[2].includes(linkedin_url)
        )
        .map(([, data]) => data)

      const response = await downloadPersonBrief(
        tokenInfo!,
        linkedin_url!,
        cachedSections
      )
      const blob = response.data as Blob
      if (!(await blobStartsWithPdfMagic(blob))) {
        let description = t("pre_meeting_brief.notif_download_failed")
        try {
          const parsed = JSON.parse(await blob.text()) as { message?: string }
          if (parsed?.message) description = parsed.message
        } catch {
          // noop
        }
        throw new Error(description)
      }
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = blobUrl
      link.download = `Pre-meeting brief ${safePersonName}.pdf`
      document.body.appendChild(link)
      link.click()
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl)
        document.body.removeChild(link)
      }, 0)
      instance.destroy()
      notificationApi.success({
        message: t("pre_meeting_brief.notif_download_success")
      })
      track(EVENTS.DOWNLOAD_PERSON_BRIEF, {
        ...getBaseEventProps({ feature: "pre_meeting_brief" }),
        account_name: safePersonName,
        download_status: "success"
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      instance.destroy()
      const description =
        error instanceof Error && error.message
          ? error.message
          : t("pre_meeting_brief.notif_download_failed")
      notificationApi.error({
        message: t("k_os.label_error"),
        description
      })
      track(EVENTS.PRE_MEETING_BRIEF_DOWNLOAD_CLICKED, {
        ...getBaseEventProps({ feature: "pre_meeting_brief" }),
        account_name: safePersonName,
        download_status: "failed",
        error_message: error
      })
    }
  }, [
    modalApi,
    personName,
    tokenInfo,
    linkedin_url,
    notificationApi,
    queryClient,
    t
  ])

  return (
    <>
      {modalContextHolder}
      {notificationContextHolder}
      <CustomTooltip
        title={
          <p className="text-xs">
            {t("pre_meeting_brief.modal_downloading_pre_meeting_brief")}
          </p>
        }
      >
        <button
          className="flex items-center gap-1 text-xs font-medium text-primary dark:text-primary-light mr-4"
          onClick={onClickDownload}
          disabled={isDisabled}
          data-testid="download-person-brief"
        >
          <Download size={14} />{" "}
          {t("pre_meeting_brief.cta_download_pre_meeting_brief")}
        </button>
      </CustomTooltip>
    </>
  )
}
